import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwEventService,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import {
  AppMetaData,
  AppMetaDataItemNames,
  FlightSchoolExaminerEntity
} from '../../../../meta-data/app-meta-data.service';
import { DateTimeUtilService } from '../../../shared/util/date-time-util.service';

@DwComponent({
  key: 'unit-member-current-status',
  name: 'Unit Member Current Status',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.FlightSchoolExaminer,
  isGlobal: false
})
@Component({
  selector: 'app-unit-member-current-status',
  templateUrl: './unit-member-current-status.component.html',
  styleUrls: ['./unit-member-current-status.component.scss']
})
export class UnitMemberCurrentStatusComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  member: FlightSchoolExaminerEntity;
  loading = true;
  errorMessage: string;

  private formApi: DwMetaDataFormApi;
  private flightSchoolExaminerId: number;
  private refreshRequested = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    dwMetaDataFormSectionStateService: DwMetaDataFormSectionStateService,
    dwMetaDataFormStateService: DwMetaDataFormStateService,
    private dwEventService: DwEventService,
    private dateTimeUtilService: DateTimeUtilService
  ) {
    super(
      dwExpressionService,
      dwMetaDataFormSectionStateService,
      dwMetaDataFormStateService,
      dwUiConfigRegistryService,
      uiMetaDataConfig,
      dwMetaDataService
    );
    this.formApi = dwMetaDataFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subscriptions.push(
      this.refreshRequested.pipe(
        debounceTime(50),
        switchMap(() => this.loadCurrentStatus())
      ).subscribe(member => {
        this.member = member;
        this.loading = false;
      })
    );

    this.subscriptions.push(
      this.formApi.getFormDataChanges<FlightSchoolExaminerEntity>(100)
        .subscribe(member => this.setMember(member))
    );

    [
      AppMetaDataItemNames.FlightSchoolExaminer,
      AppMetaDataItemNames.TrainingRecord,
      AppMetaDataItemNames.UnitMemberProfile
    ].forEach(itemName => this.subscriptions.push(
      this.dwEventService
        .getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(itemName), false, false)
        .subscribe(() => this.refresh())
    ));

    if (this.formApi.isDataLoadComplete) {
      this.setMember(this.formApi.getFormData<FlightSchoolExaminerEntity>());
    }
  }

  ngOnDestroy(): void {
    this.refreshRequested.complete();
    super.ngOnDestroy();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    // This section is informational in both view and edit modes.
  }

  refresh(): void {
    if (this.flightSchoolExaminerId) {
      this.loading = true;
      this.refreshRequested.next();
    }
  }

  get evaluatedLocal(): string {
    return this.member && this.member.TrainingCurrentEvaluatedDateTime
      ? this.dateTimeUtilService.formatDateTimeToLocal(
        this.member.TrainingCurrentEvaluatedDateTime,
        'MM/dd/yyyy h:mm a'
      )
      : '';
  }

  private setMember(member: FlightSchoolExaminerEntity): void {
    const memberId = member && member.FlightSchoolExaminerId;
    if (!memberId) {
      this.flightSchoolExaminerId = null;
      this.member = null;
      this.loading = false;
      return;
    }

    if (memberId !== this.flightSchoolExaminerId) {
      this.flightSchoolExaminerId = memberId;
      this.refresh();
    }
  }

  private loadCurrentStatus() {
    const query = AppMetaData.FlightSchoolExaminer.CreateQueryBuilder();
    query.addFilterAnd(filters => filters.Equal(
      attribute => attribute.FlightSchoolExaminerId,
      this.flightSchoolExaminerId
    ));

    this.errorMessage = null;
    return this.dwOrmDataService.executeQuery(query).pipe(
      map((members: FlightSchoolExaminerEntity[]) => members[0] || null),
      catchError(() => {
        this.errorMessage = 'The current status could not be loaded.';
        return of(null as FlightSchoolExaminerEntity);
      })
    );
  }
}
